import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "../components/Seo";
import { API_ENDPOINTS } from "../config";

export default function Products() {
  const [input, setInput] = useState<string>("");
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [backendStatus, setBackendStatus] = useState<"idle" | "checking" | "ready" | "error">("idle");

  const wakeUpBackend = async () => {
    setBackendStatus("checking");
    try {
      const res = await fetch(API_ENDPOINTS.HEALTH_CHECK, { method: "GET" });
      if (res.ok) {
        setBackendStatus("ready");
        return true;
      }
      setBackendStatus("error");
      return false;
    } catch (err) {
      console.error("Backend health check failed:", err);
      setBackendStatus("error");
      return false;
    }
  };

  const checkPrivacy = async () => {
    if (!input.trim()) {
      setError("Please enter some text to check");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);
    setSuccess(false);

    try {
      if (backendStatus !== "ready") {
        const isReady = await wakeUpBackend();
        if (!isReady) {
          throw new Error("Backend service is not responding. Please try again later.");
        }
      }

      const res = await fetch(API_ENDPOINTS.CHECK_PRIVACY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: input }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("Error response:", errorText);
        throw new Error(`Error: ${res.status} - ${errorText}`);
      }

      const data = await res.json();
      setResult(data);
      setSuccess(true);
    } catch (err: any) {
      console.error("Full error:", err);
      setError(err.message || "Unknown error occurred");
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const renderDetectors = (detectors: Record<string, any[]>) => {
    if (!detectors || Object.keys(detectors).length === 0) {
      return <div>No sensitive data detected.</div>;
    }
    return (
      <div>
        {Object.entries(detectors).map(([type, items]) => (
          <div key={type} className="mb-2">
            <div className="font-semibold">{type}:</div>
            {items && items.length > 0 ? (
              <ul className="list-disc pl-5">
                {items.map((item, idx) => (
                  <li key={idx}>
                    <span className="font-mono bg-gray-100 px-1 rounded-sm">{item.span}</span>{" "}
                    <span className="text-xs text-gray-500">
                      (start: {item.start}, end: {item.end})
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-gray-500 text-sm italic">No matches</div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const coreFeatures = [
    "🛡️ Sensitive data detection and redaction",
    "🔌 Easy Python library integration",
    "🧑‍💻 Developer Friendly API",
    "🤖 AI-Powered Detection or Fast Heuristic Detection",
    "🤖 ML trainable for custom enterprise needs",
  ];

  const promptShieldFeatures = [
    "🛡️ AI security scanning for pull request diffs",
    "💬 GitHub Actions annotations and reviewer-style feedback",
    "📄 JSON, Markdown, and SARIF outputs",
    "🔎 Detection powered in part by zero-harm-ai-detectors",
  ];

  const promptShieldDetections = [
    "Prompt injection risk",
    "PII and secret exposure",
    "Sensitive data flowing into LLMs",
    "Unsafe LLM tool or function usage",
    "Prompt and message logging",
    "System prompts containing secrets",
  ];

  return (
    <>
      <Seo
        title="Products | ZeroHarmAI Security Toolkit | Zero Harm AI"
        description="Zero Harm AI products: zero-harm-ai-gha for PR scanning, zero-harm-ai-mcp for AI safety tooling access through MCP, and the zero-harm-ai-detectors Python library for PII detection and redaction in Python applications."
        socialDescription="zero-harm-ai-gha scans pull requests for prompt injection, PII leaks, and unsafe LLM usage. zero-harm-ai-mcp brings AI safety tooling into MCP workflows. The zero-harm-ai-detectors Python library detects and redacts sensitive content in Python."
        path="/products"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Zero Harm AI Products",
          "itemListElement": [
            {
              "@type": "SoftwareApplication",
              "position": 1,
              "name": "zero-harm-ai-detectors",
              "description": "Python library for detecting and redacting PII, harmful content, and sensitive data in AI applications.",
              "applicationCategory": "DeveloperApplication",
              "operatingSystem": "Python",
              "url": "https://pypi.org/project/zero-harm-ai-detectors/",
              "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
            },
            {
              "@type": "SoftwareApplication",
              "position": 2,
              "name": "zero-harm-ai-mcp",
              "description": "MCP server for AI safety and privacy workflows with detection and redaction tooling for sensitive content.",
              "applicationCategory": "DeveloperApplication",
              "operatingSystem": "MCP",
              "url": "https://github.com/Zero-Harm-AI/zero-harm-ai-mcp",
              "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
            },
            {
              "@type": "SoftwareApplication",
              "position": 3,
              "name": "zero-harm-ai-gha",
              "description": "GitHub Action for AI security reviews in pull requests. Scans for prompt injection, PII, secrets, and unsafe LLM usage.",
              "applicationCategory": "SecurityApplication",
              "operatingSystem": "GitHub Actions",
              "url": "https://github.com/Zero-Harm-AI/zero-harm-ai-gha",
              "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
            }
          ]
        })}</script>
      </Helmet>

      <div className="p-8">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-3xl font-bold mb-6 text-center"
        >
          Products
        </motion.h1>

        <p className="text-gray-700 max-w-3xl mx-auto mb-12 text-center">
          The ZeroHarmAI open-source security toolkit helps teams detect sensitive data,
          secure agent workflows, and build stronger controls into AI delivery.
        </p>

        <section className="mb-16 max-w-6xl mx-auto">
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
            <div className="max-w-3xl">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-green-700 mb-3">
                Python Library
              </span>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">zero-harm-ai-detectors</h2>
              <p className="text-gray-700 mb-8">
                Enterprise-grade AI safety tooling that protects applications from exposing
                PII, intellectual property, and other sensitive content.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-slate-900">Core Features</h3>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-xl px-4 py-4 shadow-xs"
                >
                  <ul className="space-y-2">
                    {coreFeatures.map((feature, i) => {
                      const icon = feature.split(" ")[0];
                      const text = feature.substring(feature.indexOf(" ") + 1);
                      const isDeveloperApi = text === "Developer Friendly API";
                      return (
                        <li key={i} className="flex items-center">
                          <span className="text-lg mr-3">{icon}</span>
                          {isDeveloperApi ? (
                            <a
                              href="/docs/ai_safety/PUBLIC_API_GUIDE.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-700 hover:underline"
                            >
                              {text}
                            </a>
                          ) : (
                            <span className="text-gray-700">{text}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </motion.div>
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://pypi.org/project/zero-harm-ai-detectors/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-semibold text-center"
                  >
                    Download &amp; Install
                  </a>
                  <a
                    href="https://github.com/Zero-Harm-AI/zero-harm-ai-detectors"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-3 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 font-semibold text-center"
                  >
                    View Source
                  </a>
                </div>
              </div>

              <div>
                <motion.h3
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  className="text-xl font-semibold mb-4 text-slate-900"
                >
                  Try It Live
                </motion.h3>

                <p className="text-gray-700 mb-6">
                  Test our PII or harmful content detection right in your browser. Try entering an email, phone number, SSN or harmful text.
                </p>

                <div className="bg-white rounded-xl p-4 shadow-xs">
                  <div className="flex flex-col md:flex-row gap-6 mb-4">
                    <motion.textarea
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.6 }}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Enter text like: My email is john.doe@yahoo.com or my phone is 555-222-1234"
                      className="border p-4 w-full rounded-lg shadow-xs"
                      style={{ minHeight: "120px", flex: 1 }}
                    />
                    <motion.textarea
                      initial={{ opacity: 0 }}
                      animate={{
                        opacity: 1,
                        backgroundColor: success ? "#d1fae5" : "#f9fafb",
                      }}
                      transition={{ duration: 0.6 }}
                      value={
                        error
                          ? `Error:\n${error}`
                          : result && result.redacted
                          ? result.redacted
                          : ""
                      }
                      readOnly
                      className="border p-4 w-full rounded-lg shadow-xs bg-gray-50"
                      style={{ minHeight: "120px", flex: 1 }}
                      placeholder="Redacted text appears here..."
                    />
                  </div>

                  {error ? (
                    <div className="text-red-600 font-semibold mb-4">{error}</div>
                  ) : result && result.detectors ? (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                      <div className="font-semibold mb-2">Detected Sensitive Data:</div>
                      {renderDetectors(result.detectors)}
                    </div>
                  ) : null}

                  <div className="text-center">
                    {backendStatus === "checking" && (
                      <div className="mb-4 text-sm text-gray-600 flex items-center justify-center">
                        <svg className="animate-spin h-4 w-4 text-gray-600 inline mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                        Waking up backend service...
                      </div>
                    )}

                    <motion.button
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.6 }}
                      onClick={checkPrivacy}
                      disabled={loading || backendStatus === "checking"}
                      className={`px-8 py-3 rounded-lg text-white font-semibold ${
                        loading || backendStatus === "checking"
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-green-700 hover:bg-green-800"
                      }`}
                    >
                      {loading && (
                        <svg className="animate-spin h-5 w-5 text-white inline mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                        </svg>
                      )}
                      {backendStatus === "checking"
                        ? "Warming up backend..."
                        : loading
                        ? "Checking..."
                        : "Check for PII or Harmful Data"}
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16 max-w-6xl mx-auto">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
            <div className="max-w-3xl">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">
                GitHub Action
              </span>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">zero-harm-ai-gha</h2>
              <p className="text-gray-700 mb-8">
                zero-harm-ai-gha is a GitHub Action and CLI tool for detecting AI-specific
                security risks in pull requests before merge. It scans changed code for prompt
                injection risk, secrets exposure, PII leaks, unsafe LLM tool usage, prompt logging,
                and sensitive data flowing into LLMs.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-slate-900">Core Features</h3>
                <div className="bg-white rounded-xl px-4 py-4 shadow-xs">
                  <ul className="space-y-2">
                    {promptShieldFeatures.map((feature) => {
                      const icon = feature.split(" ")[0];
                      const text = feature.substring(feature.indexOf(" ") + 1);
                      return (
                        <li key={feature} className="flex items-center">
                          <span className="text-lg mr-3">{icon}</span>
                          <span className="text-gray-700">{text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4 text-slate-900">What It Detects</h3>
                <div className="bg-white rounded-xl px-4 py-4 shadow-xs">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {promptShieldDetections.map((item) => (
                      <li key={item} className="text-gray-700 flex items-start">
                        <span className="text-slate-600 mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="https://github.com/marketplace/actions/zero-harm-ai-gha"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-semibold text-center"
              >
                Download & Install
              </a>
              <a
                href="https://github.com/Zero-Harm-AI/zero-harm-ai-gha"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 font-semibold text-center"
              >
                View Source
              </a>
            </div>
          </div>
        </section>

        <section className="mb-16 max-w-6xl mx-auto">
          <div className="bg-cyan-50 border border-cyan-200 rounded-2xl p-8">
            <div className="max-w-3xl">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700 mb-3">
                MCP Server
              </span>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">zero-harm-ai-mcp</h2>
              <p className="text-gray-700 mb-8">
                zero-harm-ai-mcp is an MCP server for AI safety and privacy workflows. It gives
                applications and agents access to detection and redaction tooling for sensitive,
                risky, and harmful content through MCP-native integrations.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-slate-900">Core Features</h3>
                <div className="bg-white rounded-xl px-4 py-4 shadow-xs">
                  <ul className="space-y-2">
                    <li className="flex items-center">
                      <span className="text-lg mr-3">🧩</span>
                      <span className="text-gray-700">MCP-native access to AI safety tooling</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-lg mr-3">🔒</span>
                      <span className="text-gray-700">Detection and redaction for sensitive and risky content</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-lg mr-3">🤖</span>
                      <span className="text-gray-700">Designed for agent, automation, and developer workflows</span>
                    </li>
                    <li className="flex items-center">
                      <span className="text-lg mr-3">⚙️</span>
                      <span className="text-gray-700">Built to integrate with broader Zero Harm AI safety tooling</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4 text-slate-900">Use Cases</h3>
                <div className="bg-white rounded-xl px-4 py-4 shadow-xs">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <li className="text-gray-700 flex items-start">
                      <span className="text-slate-600 mr-2">•</span>
                      <span>Agent safety guardrails</span>
                    </li>
                    <li className="text-gray-700 flex items-start">
                      <span className="text-slate-600 mr-2">•</span>
                      <span>PII and secret screening</span>
                    </li>
                    <li className="text-gray-700 flex items-start">
                      <span className="text-slate-600 mr-2">•</span>
                      <span>Prompt and content redaction workflows</span>
                    </li>
                    <li className="text-gray-700 flex items-start">
                      <span className="text-slate-600 mr-2">•</span>
                      <span>MCP-powered application integrations</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
             <a
                href="https://pypi.org/project/zero-harm-ai-mcp/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-semibold text-center"
              >
                Download & Install
              </a>              
              <a
                href="https://github.com/Zero-Harm-AI/zero-harm-ai-mcp"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 font-semibold text-center"
              >
                View Source
              </a>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto">
          <div className="bg-blue-50 p-8 rounded-lg text-center">
            <h3 className="text-xl font-semibold mb-4 text-blue-800">🤝 Need Help?</h3>
            <p className="text-gray-700 mb-6">
              We build custom AI agents and AI security tooling for teams like yours.
            </p>
            <Link
              to="/contact"
              className="inline-block px-6 py-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold"
            >
              Talk to Us
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
